import { DatePicker, Form } from 'antd';
import locale from 'antd/es/date-picker/locale/uk_UA';
import moment from 'moment-timezone';
import { useCallback, useEffect, useState } from 'react';
import { useContactStore } from '../../hooks/useContactStore';
// Устанавливаем киевское время по умолчанию
moment.tz.setDefault('Europe/Kiev');

// Debug flag
const DEBUG_REQUEST_SU_WORK_FIELD = true; // переключите на false чтобы отключить логи

// Константы для типов пользователей
const USER_TYPE_TT = '101435a5-1bfb-4c55-9e79-0bffc906a2a4'; // ТТ
const USER_TYPE_CONTRACTOR = 'f3c0ce97-53e6-df11-971b-001d60e938c6'; // Підрядник

const RequestSuWorkField = ({ clickedSubmit, form, SuStartWork, SuEndWork }) => {
  // Получаем информацию о типе пользователя из useContactStore (полные объединенные данные)
  const { currentContactStore } = useContactStore();
  const userTypeId = currentContactStore?.type?.crmId;

  // Отримую ліміти з env-змінних з резервними значеннями (7 та 30)
  const maxStartDays = parseInt(process.env.REACT_APP_MAX_START_WORK_DAYS, 10) || 7;
  const maxEndDaysTT = parseInt(process.env.REACT_APP_MAX_END_WORK_DAYS_TT, 10) || 7;
  const maxEndDaysContractor = parseInt(process.env.REACT_APP_MAX_END_WORK_DAYS_CONTRACTOR, 10) || 30;

  // Определяем режим редактирования по наличию данных
  const isEditMode = !!(SuStartWork || SuEndWork);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Хелпер нормализации в Kiev TZ (на случай если DatePicker вернёт UTC)
  const toKiev = useCallback((m) => (m ? m.clone().tz('Europe/Kiev') : m), []);

  // Простая функция парсинга даты в киевском времени
  const parseDateTime = useCallback((val) => {
    if (!val) return null;
    if (DEBUG_REQUEST_SU_WORK_FIELD) console.log('[RequestSuWorkField] parseDateTime input:', val);

    if (typeof val === 'string') {
      // Парсим строку как киевское время без автоматических сдвигов
      const match = val.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?$/);
      if (match) {
        const [, date, hour, minute, second] = match;
        const result = moment(`${date} ${hour}:${minute}:${second}`, 'YYYY-MM-DD HH:mm:ss', true);
        if (DEBUG_REQUEST_SU_WORK_FIELD)
          console.log('[RequestSuWorkField] parseDateTime parsed string ->', result?.format());
        return result;
      }
    }

    if (moment.isMoment(val)) {
      if (DEBUG_REQUEST_SU_WORK_FIELD)
        console.log('[RequestSuWorkField] parseDateTime clone moment ->', val.format());
      return val.clone();
    }

    return null;
  }, []);

  // Загружаем данные при редактировании
  useEffect(() => {
    if (SuStartWork || SuEndWork) {
      const parsedStart = parseDateTime(SuStartWork);
      const parsedEnd = parseDateTime(SuEndWork);

      if (parsedStart) {
        setStartDate(parsedStart);
        form?.setFieldsValue?.({ SuStartWork: parsedStart.format('YYYY-MM-DDTHH:mm:ss.SSS') });
      }

      if (parsedEnd) {
        setEndDate(parsedEnd);
        form?.setFieldsValue?.({ SuEndWork: parsedEnd.format('YYYY-MM-DDTHH:mm:ss.SSS') });
      }
    }
  }, [SuStartWork, SuEndWork, form, parseDateTime]);

  // Ограничения для даты начала работ в зависимости от типа пользователя
  const disabledStartDate = useCallback(
    (current) => {
      const today = moment().startOf('day');

      // Базовое ограничение - не раньше сегодня
      if (current < today) {
        return true;
      }

      // Ограничения по типу пользователя: всі — максимум з env (дефолт 7) днів вперед
      if (userTypeId === USER_TYPE_TT || userTypeId === USER_TYPE_CONTRACTOR) {
        const maxDate = today.clone().add(maxStartDays, 'days');
        return current > maxDate;
      }
      // По умолчанию разрешаем все будущие даты
      return false;
    },
    [userTypeId, maxStartDays]
  );

  // Ограничения для даты окончания работ в зависимости от типа пользователя
  const disabledEndDate = useCallback(
    (current) => {
      const today = moment().tz('Europe/Kiev').startOf('day');
      const startNorm = toKiev(startDate);

      // Базовое ограничение - не раньше сегодня и не раньше даты начала
      const minDate = startNorm ? startNorm.clone().startOf('day') : today;
      if (current < minDate) {
        return true;
      }

      // Ограничения по типу пользователя
      if (userTypeId === USER_TYPE_TT) {
        // ТТ: максимум з env (дефолт 7) днів вперед від сьогодні
        const maxDate = today.clone().add(maxEndDaysTT, 'days');
        return current > maxDate;
      } else if (userTypeId === USER_TYPE_CONTRACTOR) {
        // Підрядник: максимум з env (дефолт 30) днів від дати початку робіт
        if (startNorm) {
          const maxDate = startNorm.clone().startOf('day').add(maxEndDaysContractor, 'days');
          return current > maxDate;
        }
        // Якщо дату початку ще не обрали — блокуємо всі дати (поле disabled до вибору старту)
        return true;
      }

      // По умолчанию разрешаем все будущие даты
      return false;
    },
    [startDate, userTypeId, toKiev, maxEndDaysTT, maxEndDaysContractor]
  );

  // Ограничения времени для начала работ
  const disabledStartTime = useCallback((date) => {
    if (!date) {
      // Пока не выбрана дата, блокируем все часы
      return {
        disabledHours: () => Array.from({ length: 24 }, (_, h) => h),
      };
    }

    const today = moment();
    const isSameDay = date.isSame(today, 'day');

    if (isSameDay) {
      // Для сегодняшнего дня доступны только 22 и 23 часа
      const allowed = [22, 23];
      return {
        disabledHours: () =>
          Array.from({ length: 24 }, (_, h) => h).filter((h) => !allowed.includes(h)),
      };
    }

    // Для других дней все часы доступны
    return {};
  }, []);

  // Получить разрешённые часы для старта
  const getAllowedStartHours = useCallback((date) => {
    if (!date) return [];
    const today = moment();
    if (date.isSame(today, 'day')) return [22, 23];
    return Array.from({ length: 24 }, (_, h) => h);
  }, []);

  // Получить разрешённые часы для окончания
  const getAllowedEndHours = useCallback(
    (date) => {
      if (!date) return [];
      const today = moment().tz('Europe/Kiev');
      const dNorm = toKiev(date);
      const startNorm = toKiev(startDate);
      const isSameDayAsToday = dNorm.isSame(today, 'day');
      const isSameDayAsStart = startNorm && dNorm.isSame(startNorm, 'day');
      let allowed;
      if (isSameDayAsToday) {
        allowed = [22, 23];
        if (isSameDayAsStart && startNorm) {
          // конец должен быть минимум на 1 час позже старта
          const minHour = startNorm.hour() + 1;
          allowed = allowed.filter((h) => h >= minHour);
        }
        return allowed; // может стать пустым, если старт 23:00
      }
      if (isSameDayAsStart && startNorm) {
        // Только часы строго позже часа старта
        const minHour = startNorm.hour() + 1;
        allowed = Array.from({ length: 24 }, (_, h) => h).filter((h) => h >= minHour);
        return allowed;
      }
      return Array.from({ length: 24 }, (_, h) => h);
    },
    [startDate, toKiev]
  );

  // Ограничения времени для окончания работ
  const disabledEndTime = useCallback(
    (date) => {
      if (!date) {
        // Пока не выбрана дата, блокируем все часы
        return {
          disabledHours: () => Array.from({ length: 24 }, (_, h) => h),
        };
      }
      const today = moment().tz('Europe/Kiev');
      const dNorm = toKiev(date);
      const startNorm = toKiev(startDate);
      const isSameDayAsToday = dNorm.isSame(today, 'day');
      const isSameDayAsStart = startNorm && dNorm.isSame(startNorm, 'day');

      if (isSameDayAsToday) {
        // Для сегодняшнего дня доступны только 22 и 23 часа
        let allowed = [22, 23];
        if (isSameDayAsStart) {
          // Не раньше +1 часа от старта
          const minHour = startNorm.hour() + 1;
          allowed = allowed.filter((h) => h >= minHour);
        }
        return {
          disabledHours: () =>
            Array.from({ length: 24 }, (_, h) => h).filter((h) => !allowed.includes(h)),
        };
      }

      if (isSameDayAsStart) {
        // Для того же дня что и начало - не раньше (старт + 1 час)
        const minHour = startNorm.hour() + 1;
        return {
          disabledHours: () => Array.from({ length: 24 }, (_, h) => h).filter((h) => h < minHour),
        };
      }

      // Для других дней все часы доступны
      return {};
    },
    [startDate, toKiev]
  );

  return (
    <div style={{ display: 'flex', gap: 16, paddingBottom: 24 }}>
      <Form.Item
        name="SuStartWork"
        label="Початок робіт"
        rules={[{ required: true, message: 'Оберіть початок робіт' }]}
        style={{ marginBottom: 0 }}
      >
        <span>
          <DatePicker
            allowClear={true}
            inputReadOnly
            showNow={false}
            locale={locale}
            disabledDate={disabledStartDate}
            disabled={clickedSubmit}
            showTime={{ hideDisabledOptions: true, format: 'HH:00', showNow: false }}
            disabledTime={disabledStartTime}
            format="DD-MM-YYYY HH:00"
            value={startDate}
            onOk={() => {
              /* логика перенесена в onChange */
            }}
            onChange={(val) => {
              if (DEBUG_REQUEST_SU_WORK_FIELD)
                console.log('[RequestSuWorkField] START onChange raw:', val);
              if (!val) {
                // При очистке начала работ также очищаем конец работ
                setStartDate(null);
                setEndDate(null);
                if (form) {
                  form.setFieldsValue({ SuStartWork: null, SuEndWork: null });
                }
                if (DEBUG_REQUEST_SU_WORK_FIELD)
                  console.log('[RequestSuWorkField] START cleared, END cleared');
              } else {
                if (isEditMode) {
                  // В режиме редактирования теперь тоже валидируем час, если день изменён на сегодня
                  let adjusted = val.clone().minute(0).second(0).millisecond(0);
                  const today = moment();
                  if (adjusted.isSame(today, 'day')) {
                    const allowed = getAllowedStartHours(adjusted);
                    if (DEBUG_REQUEST_SU_WORK_FIELD)
                      console.log(
                        '[RequestSuWorkField] START editMode today selected, allowed hours:',
                        allowed
                      );
                    if (allowed.length && !allowed.includes(adjusted.hour())) {
                      adjusted.hour(allowed[0]);
                      if (DEBUG_REQUEST_SU_WORK_FIELD)
                        console.log(
                          '[RequestSuWorkField] START editMode hour adjusted ->',
                          adjusted.hour()
                        );
                    }
                  }
                  setStartDate(adjusted);
                  if (form) {
                    const formattedValue = adjusted.format('YYYY-MM-DDTHH:mm:ss.SSS');
                    form.setFieldsValue({ SuStartWork: formattedValue });
                    if (DEBUG_REQUEST_SU_WORK_FIELD)
                      console.log('[RequestSuWorkField] START editMode set ->', formattedValue);
                  }
                  // При изменении початку робіт скидаємо кінець робіт
                  setEndDate(null);
                  if (form) form.setFieldsValue({ SuEndWork: null });
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.log('[RequestSuWorkField] START changed (edit mode), END reset');
                } else {
                  // Режим создания: сразу фиксируем дату с допустимым часом
                  let normalized = val.clone().minute(0).second(0).millisecond(0);
                  const allowed = getAllowedStartHours(normalized);
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.log(
                      '[RequestSuWorkField] START createMode raw ->',
                      val.format(),
                      'allowed hours:',
                      allowed
                    );
                  if (allowed.length) {
                    if (!allowed.includes(normalized.hour())) {
                      normalized.hour(allowed[0]);
                      if (DEBUG_REQUEST_SU_WORK_FIELD)
                        console.log(
                          '[RequestSuWorkField] START hour adjusted to first allowed ->',
                          normalized.hour()
                        );
                    }
                  } else {
                    // нет доступных часов -> очищаем
                    setStartDate(null);
                    if (form) form.setFieldsValue({ SuStartWork: null });
                    if (DEBUG_REQUEST_SU_WORK_FIELD)
                      console.warn('[RequestSuWorkField] START no allowed hours, cleared');
                    return;
                  }
                  setStartDate(normalized);
                  if (form) {
                    form.setFieldsValue({
                      SuStartWork: normalized.format('YYYY-MM-DDTHH:mm:ss.SSS'),
                    });
                    if (DEBUG_REQUEST_SU_WORK_FIELD)
                      console.log(
                        '[RequestSuWorkField] START normalized set ->',
                        normalized.format()
                      );
                  }
                  // Всегда очищаем конец работ при изменении початку робіт
                  setEndDate(null);
                  if (form) form.setFieldsValue({ SuEndWork: null });
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.log('[RequestSuWorkField] END reset due to START change (create mode)');
                }
              }
            }}
          />
        </span>
      </Form.Item>
      <Form.Item
        name="SuEndWork"
        label="Кінець робіт"
        rules={[
          { required: true, message: 'Оберіть кінець робіт' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const startVal = getFieldValue('SuStartWork');
              if (!value || !startVal) return Promise.resolve();
              const start = moment(startVal, 'YYYY-MM-DDTHH:mm:ss.SSS', true);
              const end = moment(value, 'YYYY-MM-DDTHH:mm:ss.SSS', true);
              if (!start.isValid() || !end.isValid()) return Promise.resolve();
              if (end.isSame(start) || end.isBefore(start)) {
                return Promise.reject(
                  new Error('Кінець робіт має бути мінімум на 1 годину пізніше початку')
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
        style={{ marginBottom: 0 }}
      >
        <span>
          <DatePicker
            allowClear={true}
            inputReadOnly
            showNow={false}
            locale={locale}
            disabledDate={disabledEndDate}
            disabled={clickedSubmit || !startDate}
            showTime={{ hideDisabledOptions: true, format: 'HH:00', showNow: false }}
            disabledTime={disabledEndTime}
            format="DD-MM-YYYY HH:00"
            value={endDate}
            onOk={() => {
              /* логика перенесена в onChange */
            }}
            onChange={(val) => {
              if (DEBUG_REQUEST_SU_WORK_FIELD)
                console.log('[RequestSuWorkField] END onChange raw:', val);
              if (!val) {
                // При очистке
                setEndDate(null);
                if (form) form.setFieldsValue({ SuEndWork: null });
                if (DEBUG_REQUEST_SU_WORK_FIELD) console.log('[RequestSuWorkField] END cleared');
                return;
              }
              // Есть значение -> нормализуем по часу
              let picked = val.clone().minute(0).second(0).millisecond(0);
              if (startDate) {
                const compStart = startDate.clone();
                if (picked.isSameOrBefore(compStart)) {
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.warn('[RequestSuWorkField] END same/before START -> shift +1h');
                  picked = compStart.clone().add(1, 'hour');
                }
              }
              if (isEditMode) {
                // Режим редактирования: сохраняем нормализованный час
                setEndDate(picked);
                if (form) {
                  const formattedValue = picked.format('YYYY-MM-DDTHH:mm:ss.SSS');
                  form.setFieldsValue({ SuEndWork: formattedValue });
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.log('[RequestSuWorkField] END editMode set ->', formattedValue);
                }
              } else {
                // Режим создания: нормализуем к допустимому часу
                let normalized = picked;
                const allowed = getAllowedEndHours(normalized);
                if (DEBUG_REQUEST_SU_WORK_FIELD)
                  console.log(
                    '[RequestSuWorkField] END createMode raw ->',
                    val.format(),
                    'allowed hours:',
                    allowed
                  );
                if (!allowed.length) {
                  setEndDate(null);
                  if (form) form.setFieldsValue({ SuEndWork: null });
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.warn('[RequestSuWorkField] END no allowed hours, cleared');
                  return;
                }
                if (!allowed.includes(normalized.hour())) {
                  normalized.hour(allowed[0]);
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.log('[RequestSuWorkField] END hour adjusted ->', normalized.hour());
                }
                if (startDate && (normalized.isSame(startDate) || normalized.isBefore(startDate))) {
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.log(
                      '[RequestSuWorkField] END was same/before START, adjusting to START+1h'
                    );
                  normalized = startDate.clone().add(1, 'hour');
                }
                setEndDate(normalized);
                if (form) {
                  form.setFieldsValue({ SuEndWork: normalized.format('YYYY-MM-DDTHH:mm:ss.SSS') });
                  if (DEBUG_REQUEST_SU_WORK_FIELD)
                    console.log('[RequestSuWorkField] END normalized set ->', normalized.format());
                }
              }
            }}
          />
        </span>
      </Form.Item>
    </div>
  );
};

export default RequestSuWorkField;
